import { Form, Input, Select, Switch } from "antd"
import { toastError, toastSuccess } from "../../utils/toast"
import ButtonBack from "../../components/button/ButtonBack"
import { ICompany, IUpdateCompany } from "../../models/company.modal"
import ButtonSubmitBack from "../../components/button/ButtonSubmitBack"
import { useLoading } from "../../context/LoadingContext"
import { apiGetListPackagesConnection, apiUpdateCompany } from "../../api"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { useCallback, useEffect, useState } from "react"
import { COMPANY, DETAIL } from "../../assets/constants/route_path"
import { IPartnerPackage } from "../../models/package.modal"
import { TRANSACTION_CONNECTION_TYPE_CONNECTION, TRANSACTION_CONNECTION_TYPE_EXTENSION } from "../../assets/constants/default_params"
import { filterOptionByLabel } from "../../utils/filter-utils"

export default function CompanyUpdate() {
    const [partnerpackageOptions, setPartnerPackageOptions] = useState<{ label: string, value: Number }[]>([])
    const [formUpdate] = Form.useForm()
    const { setIsLoading } = useLoading()
    const location = useLocation()
    const navigate = useNavigate()
    const company = location.state as ICompany
    const [searchParams] = useSearchParams()
    const cpCode = searchParams.get('cpCode')

    const fetchPartnerPackages = useCallback(async () => {
        if (!company?.code) return
        try {
            const resp = await apiGetListPackagesConnection(company?.code)
            if (resp.data?.partner_packages === null) {
                return
            }
            const options = resp.data.partner_packages.map((item: IPartnerPackage) => {
                let label = item.name
                if (item.type === TRANSACTION_CONNECTION_TYPE_CONNECTION) {
                    label += '_Đấu mới'
                }
                if (item.type === TRANSACTION_CONNECTION_TYPE_EXTENSION) {
                    label += '_Gia hạn'
                }
                return { label, value: item.id }
            })
            setPartnerPackageOptions(options)
        } catch (error) {
            console.log(error)
        }
    }, [company])

    useEffect(() => {
        if (!company) {
            navigate(COMPANY)
        }
        fetchPartnerPackages()
    }, [company, navigate, fetchPartnerPackages])

    const handleUpdate = async (values: any) => {
        try {
            setIsLoading(true)
            const dataToSubmit: IUpdateCompany = {
                id: Number(company.id),
                name: values.name || '',
                code: values.code || '',
                templateId: values.templateId || '',
                packageNewId: values.packageNewId || 0,
                supportTelesale: values.supportTelesale || false,
                description: values.description || '',
                supportChannels: []
            }
            if (values.supportApp) {
                dataToSubmit.supportChannels?.push('APP')
            }
            if (values.supportTelegram) {
                dataToSubmit.supportChannels?.push('TELEGRAM')
            }
            if (values.supportSlack) {
                dataToSubmit.supportChannels?.push('SLACK')
            }
            if (values.supportZalo) {
                dataToSubmit.supportChannels?.push('ZALO')
            }
            if (values.supportInventory) {
                dataToSubmit.supportChannels?.push('INVENTORY')
            }
            if (values.supportWarehouse) {
                dataToSubmit.supportChannels?.push('WAREHOUSE')
            }
            const resp = await apiUpdateCompany(dataToSubmit)
            if (resp.status === 200) {
                toastSuccess('Cập nhật công ty thành công!')
                formUpdate.resetFields()
                if (cpCode) {
                    navigate(COMPANY + DETAIL + `?cpCode=${cpCode}`)
                    return
                }
                navigate(COMPANY)
            } else {
                toastError('Có lỗi xảy ra!')
            }
            setIsLoading(false)
        } catch (error) {
            console.log(error);
            toastError('Có lỗi xảy ra!')
            setIsLoading(false)
        }
    }

    return (
        <div>
            <ButtonBack />
            <Form form={formUpdate} onFinish={handleUpdate} layout="vertical">
                <Form.Item name='name' label='Tên công ty' initialValue={company.name}
                    rules={[{ required: true, message: 'Vui lòng nhập tên công ty!' }]}>
                    <Input />
                </Form.Item>
                <Form.Item name='code' label='Mã công ty' initialValue={company.code}
                    rules={[{ required: true, message: 'Vui lòng nhập mã công ty!' }]}>
                    <Input />
                </Form.Item>
                <Form.Item name='templateId' label='ID Template' initialValue={company.templateId}>
                    <Input />
                </Form.Item>
                <Form.Item name='packageNewId' label='Gói cước gợi ý' initialValue={company.packageRenew?.id || company.packageNewId}>
                    <Select
                        style={{ width: '100%' }}
                        showSearch
                        placeholder="Chọn gói cước"
                        options={partnerpackageOptions}
                        filterOption={filterOptionByLabel}
                    />
                </Form.Item>
                <Form.Item label='Kênh hỗ trợ'>
                    <div style={{ display: 'flex', gap: '16px' }}>
                        <Form.Item
                            name="supportTelesale"
                            valuePropName="checked"
                            noStyle
                            initialValue={company.supportTelesale}
                        >
                            <Switch checkedChildren="TELESALE" unCheckedChildren="TELESALE" />
                        </Form.Item>
                        <Form.Item
                            name="supportApp"
                            valuePropName="checked"
                            noStyle
                            initialValue={company.supportChannels?.includes('APP')}
                        >
                            <Switch checkedChildren="APP" unCheckedChildren="APP" />
                        </Form.Item>
                        <Form.Item
                            name="supportTelegram"
                            valuePropName="checked"
                            noStyle
                            initialValue={company.supportChannels?.includes('TELEGRAM')}
                        >
                            <Switch checkedChildren="TELEGRAM" unCheckedChildren="TELEGRAM" />
                        </Form.Item>
                        <Form.Item
                            name="supportSlack"
                            valuePropName="checked"
                            noStyle
                            initialValue={company.supportChannels?.includes('SLACK')}
                        >
                            <Switch checkedChildren="SLACK" unCheckedChildren="SLACK" />
                        </Form.Item>
                        <Form.Item
                            name="supportZalo"
                            valuePropName="checked"
                            noStyle
                            initialValue={company.supportChannels?.includes('ZALO')}
                        >
                            <Switch checkedChildren="ZALO" unCheckedChildren="ZALO" />
                        </Form.Item>
                        <Form.Item
                            name="supportInventory"
                            valuePropName="checked"
                            noStyle
                            initialValue={company.supportChannels?.includes('INVENTORY')}
                        >
                            <Switch checkedChildren="INVENTORY" unCheckedChildren="INVENTORY" />
                        </Form.Item>
                        <Form.Item
                            name="supportWarehouse"
                            valuePropName="checked"
                            noStyle
                            initialValue={company.supportChannels?.includes('WAREHOUSE')}
                        >
                            <Switch checkedChildren="WAREHOUSE" unCheckedChildren="WAREHOUSE" />
                        </Form.Item>
                    </div>
                </Form.Item>
                <Form.Item name='description' label='Mô tả' initialValue={company.description}>
                    <Input />
                </Form.Item>
                <ButtonSubmitBack />
            </Form>
        </div>
    )
}
